import { useEffect, useState } from "react";
import axios, { API } from "../../api";

/**
 * Admin-only modal: create or refresh Aura's scoped Stripe Billing
 * Portal Configuration. Only visible when user.is_admin === true.
 *
 * Why this exists: Aura's Stripe account is shared with another app
 * ("Call to Arms"). By default Stripe's Portal treats every product
 * as a swap target, so customers of one app see the other app's tiers
 * in their subscription management. This modal triggers the backend
 * setup routine (POST /api/admin/billing/setup-catalog) which creates
 * an Aura-scoped Portal Configuration that allow-lists only Aura's
 * subscription products.
 *
 * Also includes a customer-inspector so you can debug "No active Aura
 * subscription" rejections without touching URLs — type an email, see
 * exactly what Stripe returned for that customer and whether each
 * subscription's price/product matched Aura's catalog.
 *
 * Safety guarantees (enforced by billing_portal_setup.py):
 *   • Never touches the account default portal config
 *   • Never deactivates or modifies any existing Stripe Price
 *   • Idempotent — safe to re-run any time
 */
export function AdminBillingPortalModal({ onClose }) {
  const [status, setStatus] = useState(null);         // { configured, config_id, known_tier_price_ids }
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);         // response from setup-catalog
  const [error, setError] = useState("");

  // Inspector state
  const [inspectEmail, setInspectEmail] = useState("");
  const [inspecting, setInspecting] = useState(false);
  const [inspection, setInspection] = useState(null);
  const [inspectError, setInspectError] = useState("");

  useEffect(() => {
    loadStatus();
  }, []);

  async function loadStatus() {
    setLoadingStatus(true);
    setError("");
    try {
      const r = await axios.get(`${API}/admin/billing/portal-status`);
      setStatus(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load status");
    } finally {
      setLoadingStatus(false);
    }
  }

  async function runSetup() {
    if (running) return;
    setRunning(true);
    setError("");
    setResult(null);
    try {
      const r = await axios.post(`${API}/admin/billing/setup-catalog`);
      setResult(r.data);
      // Refresh status after success
      await loadStatus();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Setup failed");
    } finally {
      setRunning(false);
    }
  }

  async function runInspect() {
    const email = (inspectEmail || "").trim();
    if (!email || inspecting) return;
    setInspecting(true);
    setInspectError("");
    setInspection(null);
    try {
      const r = await axios.get(
        `${API}/admin/billing/inspect-customer`,
        { params: { email } }
      );
      setInspection(r.data);
    } catch (e) {
      setInspectError(
        e?.response?.data?.detail || e.message || "Inspection failed"
      );
    } finally {
      setInspecting(false);
    }
  }

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      data-testid="admin-billing-portal-modal"
    >
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 720 }}
      >
        <div className="modal-header">
          <h2>Stripe Billing Portal — Scoped Configuration</h2>
          <button
            className="modal-close"
            onClick={onClose}
            data-testid="admin-billing-portal-close"
          >
            ×
          </button>
        </div>

        <div className="modal-body" style={{ lineHeight: 1.55 }}>
          <p style={{ opacity: 0.85, fontSize: "0.92em", marginBottom: 16 }}>
            Aura's Stripe account is shared with another app. This creates
            (or refreshes) a dedicated Portal Configuration that scopes the
            "Manage Subscription" view to Aura's own products only — the
            account default is left untouched, and no existing prices are
            ever modified.
          </p>

          {loadingStatus ? (
            <div style={{ padding: 16 }} data-testid="admin-billing-portal-loading">
              Loading status…
            </div>
          ) : status ? (
            <div
              style={{
                padding: 12,
                borderRadius: 8,
                background: status.configured ? "rgba(74,222,128,0.10)" : "rgba(250,204,21,0.10)",
                border: `1px solid ${status.configured ? "rgba(74,222,128,0.35)" : "rgba(250,204,21,0.35)"}`,
                marginBottom: 16,
              }}
              data-testid="admin-billing-portal-status"
            >
              {status.configured ? (
                <>
                  <strong>✓ Scoped configuration active</strong>
                  <div style={{ opacity: 0.75, fontSize: "0.85em", marginTop: 4 }}>
                    config id: <code>{status.config_id}</code>
                  </div>
                </>
              ) : (
                <>
                  <strong>! Not yet set up — using account default</strong>
                  <div style={{ opacity: 0.75, fontSize: "0.85em", marginTop: 4 }}>
                    Click "Run setup" to create the Aura-scoped configuration.
                  </div>
                </>
              )}
            </div>
          ) : null}

          <button
            className="btn-primary"
            onClick={runSetup}
            disabled={running}
            data-testid="admin-billing-portal-run"
            style={{ marginBottom: 12 }}
          >
            {running ? "Running…" : status?.configured ? "Refresh configuration" : "Run setup"}
          </button>

          {error && (
            <div
              style={{
                padding: 12,
                borderRadius: 8,
                background: "rgba(248,113,113,0.12)",
                border: "1px solid rgba(248,113,113,0.4)",
                color: "#fca5a5",
                marginTop: 12,
              }}
              data-testid="admin-billing-portal-error"
            >
              {error}
            </div>
          )}

          {result && (
            <div
              style={{ marginTop: 16 }}
              data-testid="admin-billing-portal-result"
            >
              <div
                style={{
                  padding: 12,
                  borderRadius: 8,
                  background: result.ok
                    ? "rgba(74,222,128,0.10)"
                    : "rgba(248,113,113,0.10)",
                  border: `1px solid ${result.ok ? "rgba(74,222,128,0.35)" : "rgba(248,113,113,0.35)"}`,
                }}
              >
                <strong>
                  {result.ok
                    ? `✓ ${result.action === "created" ? "Created" : "Updated"} — config ${result.config_id}`
                    : `✗ ${result.reason || "aborted"}`}
                </strong>
              </div>

              {result.products && result.products.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <strong>Allow-listed products ({result.products.length}):</strong>
                  <ul style={{ marginTop: 6, paddingLeft: 20 }}>
                    {result.products.map((p) => (
                      <li
                        key={p.product}
                        style={{ fontSize: "0.88em", opacity: 0.88 }}
                      >
                        <code>{p.product}</code> — {p.prices.length} price
                        {p.prices.length === 1 ? "" : "s"}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.warnings && result.warnings.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <strong style={{ color: "#facc15" }}>
                    Warnings ({result.warnings.length}):
                  </strong>
                  <ul
                    style={{
                      marginTop: 6,
                      paddingLeft: 20,
                      fontSize: "0.85em",
                      opacity: 0.85,
                    }}
                    data-testid="admin-billing-portal-warnings"
                  >
                    {result.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* ── Customer Inspector ─────────────────────────────────
              Paste a customer email → see every subscription Stripe
              returned for them and whether each price/product matched
              Aura's catalog. Read-only. Use this when a real Aura
              subscriber gets "No active Aura subscription to manage". */}
          <div
            style={{
              marginTop: 24,
              paddingTop: 16,
              borderTop: "1px solid rgba(255,255,255,0.08)",
            }}
            data-testid="admin-billing-inspect-section"
          >
            <strong>Inspect customer</strong>
            <div style={{ opacity: 0.7, fontSize: "0.85em", marginTop: 4, marginBottom: 10 }}>
              Type an email to see whether Stripe treats them as an Aura
              subscriber and why. Read-only.
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <input
                type="email"
                placeholder="customer@example.com"
                value={inspectEmail}
                onChange={(e) => setInspectEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runInspect()}
                data-testid="admin-billing-inspect-email"
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.15)",
                  background: "rgba(0,0,0,0.25)",
                  color: "inherit",
                }}
              />
              <button
                className="btn-primary"
                onClick={runInspect}
                disabled={inspecting || !inspectEmail.trim()}
                data-testid="admin-billing-inspect-run"
              >
                {inspecting ? "Looking up…" : "Inspect"}
              </button>
            </div>

            {inspectError && (
              <div
                style={{
                  padding: 12,
                  borderRadius: 8,
                  background: "rgba(248,113,113,0.12)",
                  border: "1px solid rgba(248,113,113,0.4)",
                  color: "#fca5a5",
                  fontSize: "0.88em",
                }}
                data-testid="admin-billing-inspect-error"
              >
                {inspectError}
              </div>
            )}

            {inspection && (
              <div data-testid="admin-billing-inspect-result">
                {(!inspection.customers || inspection.customers.length === 0) ? (
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      background: "rgba(250,204,21,0.10)",
                      border: "1px solid rgba(250,204,21,0.35)",
                      fontSize: "0.88em",
                    }}
                  >
                    No Stripe customer records found for{" "}
                    <code>{inspection.email}</code>.
                  </div>
                ) : (
                  inspection.customers.map((c, ci) => {
                    const insp = c.inspection || {};
                    const resolvedId = insp.resolved_aura_subscription_id;
                    return (
                      <div
                        key={c.customer?.id || ci}
                        style={{
                          marginBottom: 12,
                          padding: 12,
                          borderRadius: 8,
                          background: resolvedId
                            ? "rgba(74,222,128,0.10)"
                            : "rgba(248,113,113,0.10)",
                          border: `1px solid ${resolvedId ? "rgba(74,222,128,0.35)" : "rgba(248,113,113,0.35)"}`,
                          fontSize: "0.86em",
                        }}
                      >
                        <div style={{ marginBottom: 6 }}>
                          <strong>
                            {resolvedId ? "✓ Aura subscriber" : "✗ Not an Aura subscriber"}
                          </strong>{" "}
                          — customer <code>{c.customer?.id}</code>
                        </div>
                        {resolvedId && (
                          <div style={{ opacity: 0.8, marginBottom: 6 }}>
                            Aura sub: <code>{resolvedId}</code>
                          </div>
                        )}
                        {insp.subscriptions && insp.subscriptions.length > 0 ? (
                          <div style={{ marginTop: 8 }}>
                            <div style={{ opacity: 0.7, marginBottom: 4 }}>
                              Subscriptions ({insp.subscriptions.length}):
                            </div>
                            <ul style={{ marginTop: 0, paddingLeft: 18 }}>
                              {insp.subscriptions.map((s) => (
                                <li key={s.subscription_id} style={{ marginBottom: 4 }}>
                                  <code>{s.subscription_id}</code>{" "}
                                  <span style={{ opacity: 0.65 }}>({s.status})</span>
                                  {" — "}
                                  {s.matched_as_aura_via ? (
                                    <span style={{ color: "#4ade80" }}>
                                      Aura via {s.matched_as_aura_via}
                                    </span>
                                  ) : (
                                    <span style={{ opacity: 0.65 }}>not Aura</span>
                                  )}
                                  {s.items && s.items.length > 0 && (
                                    <ul style={{ marginTop: 2, paddingLeft: 20, opacity: 0.75 }}>
                                      {s.items.map((it, ii) => (
                                        <li key={ii} style={{ fontSize: "0.9em" }}>
                                          price <code>{it.price_id}</code> →
                                          {" "}product <code>{it.product_id}</code>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <div style={{ opacity: 0.65, marginTop: 4 }}>
                            No subscriptions on this customer.
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
